'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Mail } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { validateDeclarationAction } from '@/actions/declaracao/validate-declaration-action'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import warningImg from '@/public/img/warning.svg'
import { ContactSupportModal } from './contact-support-modal'
import DeclarationValidateCard from './declaration-validate-card'

const declarationValidationFormSchema = z.object({
  code: z.string().min(1, {
    message: 'Insirar um código válido.',
  }),
})

export interface ResultDataDeclaration {
  message: string
  status: string
  object: {
    object: string
    id: string
    exam_score: string
    user: {
      id: string
      full_name: string
      preferred_name: string
      email: string
      document_number: string
      phone_number: string
    }
    product: {
      id: string
      name: string
    }
    enrolment_id: string
    document_url: string
    document_copy_url: string
    expires_at: string
    status: string
  }
  method: 'GET'
  code: number
}

type DeclarationValidationFormSchema = z.infer<
  typeof declarationValidationFormSchema
>

export function DeclarationValidationForm({ code }: { code?: string }) {
  const [success, setSuccess] = useState<boolean | null>(null)
  const [data, setData] = useState<ResultDataDeclaration | null>(null)
  const [isPending, startTransition] = useTransition()
  const form = useForm<DeclarationValidationFormSchema>({
    resolver: zodResolver(declarationValidationFormSchema),
  })

  const handleValidateDeclaration = useCallback(
    (values: DeclarationValidationFormSchema) => {
      try {
        startTransition(async () => {
          const result = await validateDeclarationAction({
            code: values.code,
          })
          if (!result.ok) {
            setSuccess(false)
            toast.error('Código inválido.')
            return
          }
          setSuccess(true)
          setData(result.data)
          toast.success('Código válido.')
        })
      } catch (error) {
        console.error('Form submission error', error)
        toast.error('Failed to submit the form. Please try again.')
      }
    },
    []
  )

  useEffect(() => {
    if (code) {
      form.setValue('code', code)
      handleValidateDeclaration({ code })
    }
  }, [code, form, handleValidateDeclaration])

  return (
    <Form {...form}>
      <form
        className="container pb-10 md:pb-21"
        onSubmit={form.handleSubmit(handleValidateDeclaration)}
      >
        <div className="-mt-16 mx-auto flex w-full max-w-133.5 flex-col items-center justify-center gap-2 rounded-xl bg-white px-6 py-8 shadow-md md:p-10">
          <header className="flex flex-col items-center justify-center text-center">
            {/* bg-[#6A1BE0] */}
            <div className="rounded-1xl bg-primary-500 p-2">
              <Mail className="text-primary-50" />
            </div>

            <div className="mt-4">
              <h3 className="font-medium text-2xl text-primary-900 leading-none">
                Código de Declaração
              </h3>
              <p className="mt-0.5 font-normal text-[#ABACAD] text-sm leading-none">
                Informe o código de autenticidade no campo abaixo:
              </p>
            </div>
          </header>

          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="mt-3 w-full space-y-0">
                <FormLabel className="z-10 ml-5 bg-white px-1 font-medium text-[#656566] text-xs">
                  Informe o código
                </FormLabel>
                <FormControl>
                  <Input
                    className="-mt-2.5 h-12 rounded border border-[#D7D7D9] bg-transparent bg-white text-sm placeholder:text-[#ABACAD]"
                    placeholder="Código da Declaração"
                    type=""
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {data && success === true && (
            <>
              <DeclarationValidateCard
                courseName={data.object.product.name}
                documentUrl={data.object.document_copy_url}
                expiredAt={data.object.expires_at}
                userFullName={data.object.user.full_name}
              />
              {/* <div>
                <div className="mt-5 flex items-center gap-4 rounded bg-[#D0FFE1] p-6">
                  <CircleCheckBig className="text-green-700" />
                  <div>
                    <span className="text-2.5xl font-medium uppercase text-green-500">
                      VÁLIDO
                    </span>
                    <p className="text-sm font-normal text-[#656566]">
                      Código informado pertence a nossa base de dados.
                    </p>
                  </div>
                </div>
              </div> */}
            </>
          )}
          {success === false && (
            <div className="mt-5 flex items-center gap-4 rounded bg-[#FFEBD0] p-6">
              <Image
                alt="Imagem de aviso"
                height={48}
                src={warningImg}
                width={48}
              />
              <div>
                <span className="font-medium text-2.5xl text-[#2E2E2E] uppercase">
                  INVÁLIDO
                </span>
                <p className="font-normal text-[#656566] text-sm">
                  Código informado não pertence a nossa base de dados.
                </p>
              </div>
            </div>
          )}
          {/* <div className="mt-5 flex items-center gap-4 rounded bg-[#FFEBD0] p-6">
            <Image
              src={warningImg}
              alt="Imagem de aviso"
              width={48}
              height={48}
            />
            <div>
              <span className="text-2.5xl font-medium uppercase text-[#2E2E2E]">
                INVÁLIDO
              </span>
              <p className="text-sm font-normal text-[#656566]">
                Código informado não pertence a nossa base de dados.
              </p>
            </div>
          </div> */}

          <div className="mt-6 w-full">
            <ContactSupportModal />

            <div className="mt-5 flex justify-end">
              <Button
                className="h-10 max-w-44.5 text-sm"
                disabled={isPending}
                type="submit"
              >
                {isPending ? 'Verificando...' : 'Verificar Autenticidade'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  )
}
